import { PersonProjectI } from "../../../../../../api/request/Lead/Interfaces/PersonProjectInterface";

/**
 * Pasos del funnel native en orden (ms-agl `DbalNativeLeadStatus`). Sirven para
 * saber qué alta abierta va más avanzada cuando la persona tiene varias: p. ej.
 * la legacy que el JIT migró a native, parada en la oferta, y la native que ya
 * está en la nota legal. Ordenadas por actividad, la vieja salía primero y su
 * «Continua» llevaba al cliente a la oferta (bobolo, 30-oct-2026).
 */
const FUNNEL_STEPS = [
  [
    "initialQuestions",
    ["initial_status", "product_questions_pending", "prestack_pending"],
  ],
  ["medical", ["questions_pending"]],
  ["advisor", ["advisor_pending"]],
  ["offer", ["upsell_pending"]],
  ["payment", ["payment_pending", "paymentmethodpending_pending"]],
  ["personalData", ["nationality_pending"]],
  ["address", ["address_pending"]],
  [
    "beneficiaries",
    [
      "beneficiaries_pending",
      "beneficiariestypes_pending",
      "physicalperson_pending",
      "bankingentity_pending",
      "legalperson_pending",
    ],
  ],
  ["idCard", ["idcard_pending"]],
  ["consents", ["consents_pending"]],
  ["legalNote", ["legalnote_pending"]],
  [
    "signature",
    ["signature_pending", "payment_validation_pending", "validation_pending"],
  ],
] as const;

export type FunnelStepT = (typeof FUNNEL_STEPS)[number][0];

/** Paso del funnel de un estado y su posición; null si no es un paso del funnel. */
export const funnelStep = (
  status: string,
): { step: FunnelStepT; rank: number } | null => {
  const rank = FUNNEL_STEPS.findIndex(([, statuses]) =>
    (statuses as readonly string[]).includes(status),
  );
  return -1 === rank ? null : { step: FUNNEL_STEPS[rank][0], rank };
};

/**
 * Solo se retoman las native vigentes, también la de la sesión: un lead legacy
 * sin cotización en native no tiene funnel native al que volver (el cuestionario
 * da NO_QUOTE y la pantalla entraba en bucle). Ese cliente empieza una nueva.
 */
export const isContinuable = (p: PersonProjectI): boolean =>
  "EXPIRED" !== p.state && p.native;

/** Alta abierta: se puede retomar y aún no es póliza. */
const isOpen = (p: PersonProjectI): boolean =>
  "POLICY" !== p.state && isContinuable(p);

/**
 * - LEAD: el alta en curso (la abierta más avanzada).
 * - PREVIOUS: abierta, más atrás que la en curso y anterior a ella.
 * - BEHIND: abierta, más atrás que la en curso pero posterior.
 * - OPEN: abierta, tan avanzada como la en curso.
 * - CLOSED: póliza, caducada o legacy sin funnel native.
 */
export type QuoteRoleT = "LEAD" | "PREVIOUS" | "BEHIND" | "OPEN" | "CLOSED";

export interface ArrangedQuoteI {
  project: PersonProjectI;
  role: QuoteRoleT;
  step: FunnelStepT | null;
}

export interface ArrangedQuotesI {
  quotes: ArrangedQuoteI[];
  lead: ArrangedQuoteI | null;
}

const sameLead = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();

const rankOf = (p: PersonProjectI): number => funnelStep(p.status)?.rank ?? -1;

const createdBefore = (a: PersonProjectI, b: PersonProjectI): boolean => {
  const ta = Date.parse(a.createdAt);
  const tb = Date.parse(b.createdAt);
  if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta < tb;
  // Los ULID llevan el instante de creación delante.
  return a.leadUlid.toUpperCase() < b.leadUlid.toUpperCase();
};

/**
 * Alta en curso: la abierta más avanzada en el funnel; si empatan, la de la
 * sesión y, si no, la de actividad más reciente (la lista llega así ordenada).
 * Va la primera de las no pólizas; las pólizas no se mueven de su sitio.
 */
export const arrangeQuotes = (
  projects: PersonProjectI[],
  currentLeadId: string,
): ArrangedQuotesI => {
  const lead = projects
    .filter(isOpen)
    .reduce<PersonProjectI | null>((best, p) => {
      if (null === best) return p;
      const diff = rankOf(p) - rankOf(best);
      if (0 !== diff) return 0 < diff ? p : best;
      return sameLead(p.leadUlid, currentLeadId) ? p : best;
    }, null);

  const roleOf = (p: PersonProjectI): QuoteRoleT => {
    if (!isOpen(p)) return "CLOSED";
    if (null === lead) return "OPEN";
    if (p === lead) return "LEAD";
    if (rankOf(p) >= rankOf(lead)) return "OPEN";
    return createdBefore(p, lead) ? "PREVIOUS" : "BEHIND";
  };
  const arrange = (p: PersonProjectI): ArrangedQuoteI => ({
    project: p,
    role: roleOf(p),
    step: funnelStep(p.status)?.step ?? null,
  });

  const movable = projects.filter((p) => "POLICY" !== p.state);
  const queue =
    null === lead ? movable : [lead, ...movable.filter((p) => p !== lead)];
  const quotes = projects.map((p) =>
    arrange("POLICY" === p.state ? p : (queue.shift() as PersonProjectI)),
  );

  return {
    quotes,
    lead: quotes.find((q) => "LEAD" === q.role) ?? null,
  };
};
